// System Health Monitoring Service
// Extends existing monitoring infrastructure with system-level health checks

import { monitoringService } from './monitoringService';
import Logger from './logger';

interface SystemResourceMetrics {
  cpu: {
    usage: number; // percentage
    load: number; // 1-minute load average
    temperature?: number; // Celsius (if available)
  };
  memory: {
    total: number; // MB
    used: number; // MB
    available: number; // MB
    usage: number; // percentage
  };
  disk: {
    total: number; // GB
    used: number; // GB
    available: number; // GB
    usage: number; // percentage
    readSpeed: number; // MB/s
    writeSpeed: number; // MB/s
  };
  network: {
    bytesIn: number; // MB
    bytesOut: number; // MB
    connections: number;
    latency: number; // ms
  };
}

interface ServiceHealthStatus {
  name: string;
  status: 'healthy' | 'degraded' | 'unhealthy' | 'unknown';
  responseTime: number; // ms
  lastCheck: number;
  error?: string;
  uptime: number; // seconds
}

interface AlertThresholds {
  cpuUsage: number;
  memoryUsage: number;
  diskUsage: number;
  responseTime: number;
}

interface SystemHealthSummary {
  overallStatus: 'healthy' | 'degraded' | 'unhealthy';
  healthScore: number; // 0-100
  lastUpdated: number;
  resources: SystemResourceMetrics;
  services: ServiceHealthStatus[];
  alerts: string[];
  recommendations: string[];
}

class SystemHealthService {
  private isMonitoring = false;
  private monitoringInterval: NodeJS.Timeout | null = null;
  private healthData: SystemHealthSummary | null = null;
  private serviceEndpoints: Map<string, string> = new Map();
  private alertThresholds: AlertThresholds = {
    cpuUsage: 80, // Alert if CPU > 80%
    memoryUsage: 85, // Alert if memory > 85%
    diskUsage: 90, // Alert if disk > 90%
    responseTime: 2000, // Alert if response time > 2s
  };

  constructor() {
    this.initializeServiceEndpoints();
    this.startMonitoring();
  }

  private initializeServiceEndpoints(): void {
    // Initialize with common service endpoints
    this.serviceEndpoints.set('database', '/api/health/database');
    this.serviceEndpoints.set('auth', '/api/health/auth');
    this.serviceEndpoints.set('storage', '/api/health/storage');
    this.serviceEndpoints.set('external-api', '/api/health/external');
  }

  private startMonitoring(): void {
    if (this.isMonitoring) return;

    this.isMonitoring = true;
    this.monitoringInterval = setInterval(() => {
      this.performHealthCheck();
    }, 30000); // Check every 30 seconds

    // Perform initial health check
    this.performHealthCheck();

  }

  private async performHealthCheck(): Promise<void> {
    try {
      const resources = await this.getSystemResources();
      const services = await this.checkServiceHealth();
      const healthScore = this.calculateHealthScore(resources, services);
      const alerts = this.generateAlerts(resources, services);
      const recommendations = this.generateRecommendations(resources, services);

      this.healthData = {
        overallStatus: this.determineOverallStatus(healthScore),
        healthScore,
        lastUpdated: Date.now(),
        resources,
        services,
        alerts,
        recommendations
      };

      // Record health check performance
      monitoringService.recordPerformance('system_health_check', 0, true);
    } catch (error) {
      Logger.error('System health check failed:', error as Error);
      monitoringService.recordError('System health check failed', error instanceof Error ? error.stack : undefined);
    }
  }

  private async getSystemResources(): Promise<SystemResourceMetrics> {
    // In a real implementation, this would call system APIs
    // For now, we'll simulate with realistic values
    const now = Date.now();
    
    // Simulate CPU usage with some variation
    const baseCpuUsage = 45 + Math.sin(now / 10000) * 20;
    const cpuUsage = Math.max(0, Math.min(100, baseCpuUsage));
    
    // Simulate memory usage
    const totalMemory = 8192; // 8GB
    const usedMemory = 4096 + Math.sin(now / 15000) * 1024;
    const availableMemory = totalMemory - usedMemory;
    
    // Simulate disk usage
    const totalDisk = 500; // 500GB
    const usedDisk = 350 + Math.sin(now / 20000) * 50;
    const availableDisk = totalDisk - usedDisk;
    
    // Simulate network metrics
    const baseLatency = 50 + Math.sin(now / 8000) * 30;
    const latency = Math.max(10, Math.min(200, baseLatency));

    return {
      cpu: {
        usage: cpuUsage,
        load: cpuUsage / 100 * 4, // Simulate load average
        temperature: 45 + (cpuUsage / 100) * 20 // Simulate temperature
      },
      memory: {
        total: totalMemory,
        used: usedMemory,
        available: availableMemory,
        usage: (usedMemory / totalMemory) * 100
      },
      disk: {
        total: totalDisk,
        used: usedDisk,
        available: availableDisk,
        usage: (usedDisk / totalDisk) * 100,
        readSpeed: 100 + Math.random() * 50,
        writeSpeed: 50 + Math.random() * 30
      },
      network: {
        bytesIn: 1024 + Math.random() * 512,
        bytesOut: 512 + Math.random() * 256,
        connections: 150 + Math.floor(Math.random() * 100),
        latency: latency
      }
    };
  }

  private async checkServiceHealth(): Promise<ServiceHealthStatus[]> {
    const services: ServiceHealthStatus[] = [];
    
    for (const [name, endpoint] of this.serviceEndpoints) {
      try {
        const startTime = typeof performance !== 'undefined' ? performance.now() : Date.now();
        
        // Simulate service health check
        const isHealthy = Math.random() > 0.1; // 90% healthy
        const responseTime = 50 + Math.random() * 150; // 50-200ms
        
        let status: 'healthy' | 'degraded' | 'unhealthy' | 'unknown';
        if (isHealthy) {
          status = responseTime < 100 ? 'healthy' : 'degraded';
        } else {
          status = 'unhealthy';
        }

        services.push({
          name,
          status,
          responseTime,
          lastCheck: Date.now(),
          uptime: Math.floor(Math.random() * 86400) + 3600, // 1-24 hours
          error: isHealthy ? undefined : 'Service temporarily unavailable'
        });

        // Record service health check
        monitoringService.recordPerformance(`service_health_${name}`, responseTime, isHealthy);
      } catch (error) {
        services.push({
          name,
          status: 'unhealthy',
          responseTime: 0,
          lastCheck: Date.now(),
          uptime: 0,
          error: error instanceof Error ? error.message : 'Unknown error'
        });
      }
    }

    return services;
  }

  private calculateHealthScore(resources: SystemResourceMetrics, services: ServiceHealthStatus[]): number {
    let score = 100;

    // Resource scoring
    if (resources.cpu.usage > this.alertThresholds.cpuUsage) {
      score -= 15;
    }
    if (resources.memory.usage > this.alertThresholds.memoryUsage) {
      score -= 15;
    }
    if (resources.disk.usage > this.alertThresholds.diskUsage) {
      score -= 10;
    }
    if (resources.network.latency > this.alertThresholds.responseTime) {
      score -= 10;
    }

    // Service scoring
    const unhealthyServices = services.filter(s => s.status === 'unhealthy').length;
    const degradedServices = services.filter(s => s.status === 'degraded').length;
    
    score -= unhealthyServices * 20;
    score -= degradedServices * 10;

    return Math.max(0, Math.min(100, score));
  }

  private determineOverallStatus(healthScore: number): 'healthy' | 'degraded' | 'unhealthy' {
    if (healthScore >= 80) return 'healthy';
    if (healthScore >= 50) return 'degraded';
    return 'unhealthy';
  }

  private generateAlerts(resources: SystemResourceMetrics, services: ServiceHealthStatus[]): string[] {
    const alerts: string[] = [];

    if (resources.cpu.usage > this.alertThresholds.cpuUsage) {
      alerts.push(`High CPU usage: ${resources.cpu.usage.toFixed(1)}%`);
    }
    if (resources.memory.usage > this.alertThresholds.memoryUsage) {
      alerts.push(`High memory usage: ${resources.memory.usage.toFixed(1)}%`);
    }
    if (resources.disk.usage > this.alertThresholds.diskUsage) {
      alerts.push(`High disk usage: ${resources.disk.usage.toFixed(1)}%`);
    }
    if (resources.network.latency > this.alertThresholds.responseTime) {
      alerts.push(`High network latency: ${resources.network.latency.toFixed(0)}ms`);
    }

    services.forEach(service => {
      if (service.status === 'unhealthy') {
        alerts.push(`Service ${service.name} is unhealthy: ${service.error}`);
      }
    });

    return alerts;
  }

  private generateRecommendations(resources: SystemResourceMetrics, services: ServiceHealthStatus[]): string[] {
    const recommendations: string[] = [];

    if (resources.cpu.usage > 70) {
      recommendations.push('Consider scaling CPU resources or optimizing CPU-intensive operations');
    }
    if (resources.memory.usage > 80) {
      recommendations.push('Consider increasing memory allocation or implementing memory optimization');
    }
    if (resources.disk.usage > 85) {
      recommendations.push('Consider disk cleanup or increasing storage capacity');
    }
    if (resources.network.latency > 100) {
      recommendations.push('Investigate network performance issues or consider CDN implementation');
    }

    const unhealthyServices = services.filter(s => s.status === 'unhealthy');
    if (unhealthyServices.length > 0) {
      recommendations.push(`Investigate and restart ${unhealthyServices.length} unhealthy service(s)`);
    }

    return recommendations;
  }

  // Public API methods
  public async getSystemHealth(): Promise<SystemHealthSummary> {
    if (!this.healthData) {
      await this.performHealthCheck();
    }
    return this.healthData!;
  }

  public getHealthScore(): number {
    return this.healthData?.healthScore || 0;
  }

  public getOverallStatus(): 'healthy' | 'degraded' | 'unhealthy' {
    return this.healthData?.overallStatus || 'healthy';
  }

  public getAlerts(): string[] {
    return this.healthData?.alerts || [];
  }

  public getRecommendations(): string[] {
    return this.healthData?.recommendations || [];
  }

  public async forceHealthCheck(): Promise<void> {
    await this.performHealthCheck();
  }

  public addServiceEndpoint(name: string, endpoint: string): void {
    this.serviceEndpoints.set(name, endpoint);
  }

  public removeServiceEndpoint(name: string): void {
    this.serviceEndpoints.delete(name);
  }

  public updateAlertThresholds(thresholds: Partial<AlertThresholds>): void {
    this.alertThresholds = { ...this.alertThresholds, ...thresholds };
  }

  public stopMonitoring(): void {
    if (this.monitoringInterval) {
      clearInterval(this.monitoringInterval);
      this.monitoringInterval = null;
    }
    this.isMonitoring = false;

  }
}

// Create singleton instance
const systemHealthService = new SystemHealthService();

// Make available globally for debugging
if (typeof window !== 'undefined') {
  (window as any).systemHealthService = systemHealthService;
}

export default systemHealthService;
export type { 
  SystemResourceMetrics, 
  ServiceHealthStatus, 
  SystemHealthSummary 
};
